import React, { useEffect, useRef } from 'react';
import { DomainCard } from '~/components/domain-card/index';
import { Button } from '~/components/ui/button';
import { Trash2 } from 'lucide-react';
import type { Route as FavoritesRoute } from './+types/favorites';
import { useTranslation } from 'react-i18next';
import type { DomainSuggestion } from '~/types/domain'; // Import DomainSuggestion type
import {
  useFavoritesStore,
  useIsFavoritesLoaded,
} from '~/stores/favorites-store'; // Import the new store hook and loader status hook
// Removed shallow import again
import { animate } from 'motion';
import type { Tld } from '~/config/tld-config'; // Import Tld type
import { TLD_VALUES } from '~/config/tld-lists'; // Import TLD_VALUES for validation

// Motion components for animation
interface MotionProps {
  initial?: Record<string, unknown>;
  animate?: Record<string, unknown>;
  transition?: Record<string, unknown>;
  whileHover?: Record<string, unknown>;
  whileTap?: Record<string, unknown>;
}

// Motion Div component
function MotionDiv({
  initial,
  animate: animateProp,
  transition,
  whileHover,
  whileTap,
  children,
  ...htmlProps
}: React.HTMLAttributes<HTMLDivElement> & MotionProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && animateProp) {
      animate(ref.current, animateProp, transition);
    }
  }, [animateProp, transition]);

  // Handle hover and tap animations
  useEffect(() => {
    if (!ref.current || !whileHover) return;

    const handleMouseEnter = () => {
      if (ref.current) animate(ref.current, whileHover);
    };

    const handleMouseLeave = () => {
      if (ref.current && animateProp) animate(ref.current, animateProp);
    };

    ref.current.addEventListener('mouseenter', handleMouseEnter);
    ref.current.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (ref.current) {
        ref.current.removeEventListener('mouseenter', handleMouseEnter);
        ref.current.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [whileHover, animateProp]);

  // Handle tap animations
  useEffect(() => {
    if (!ref.current || !whileTap) return;

    const handleMouseDown = () => {
      if (ref.current) animate(ref.current, whileTap);
    };

    const handleMouseUp = () => {
      if (ref.current && animateProp) animate(ref.current, animateProp);
    };

    ref.current.addEventListener('mousedown', handleMouseDown);
    ref.current.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (ref.current) {
        ref.current.removeEventListener('mousedown', handleMouseDown);
        ref.current.removeEventListener('mouseup', handleMouseUp);
      }
    };
  }, [whileTap, animateProp]);

  // Apply initial styles via inline style
  const style = {
    ...htmlProps.style,
    ...(initial ? initial : {}),
  };

  return (
    <div ref={ref} {...htmlProps} style={style}>
      {children}
    </div>
  );
}

const Motion = {
  div: MotionDiv,
};

export const meta: FavoritesRoute.MetaFunction = () => {
  return [
    { title: 'Saved Domains - imaginame.io' },
    {
      name: 'description',
      content:
        "View and manage your saved domain names with imaginame.io's favorites feature.",
    },
    { property: 'og:title', content: 'Saved Domains - imaginame.io' },
    {
      property: 'og:description',
      content:
        "View and manage your saved domain names with imaginame.io's favorites feature.",
    },
  ];
};

export default function Favorites() {
  const { t } = useTranslation();
  // Split state selection into individual hooks
  const favorites = useFavoritesStore((state) => state.favorites);
  const clearAllFavorites = useFavoritesStore(
    (state) => state.clearAllFavorites
  );
  const isFavorite = useFavoritesStore((state) => state.isFavorite);
  const isStoreLoaded = useIsFavoritesLoaded(); // Use the loading status hook

  const handleClearAll = () => {
    if (window.confirm(t('favorites.confirmClear'))) {
      clearAllFavorites();
    }
  };

  return (
    <Motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 pt-8">
        <Motion.div
          className="mx-auto max-w-6xl"
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Motion.div
            className="mb-8 flex items-center justify-between"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold">{t('favorites.title')}</h1>
              {/* Show count only when loaded */}
              {isStoreLoaded && (
                <span className="bg-primary/90 text-primary-foreground rounded-full px-2.5 py-0.5 text-sm font-medium">
                  {favorites.length}
                </span>
              )}
            </div>

            {/* Show button only when loaded and favorites exist */}
            {isStoreLoaded && favorites.length > 0 && (
              <Motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleClearAll}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {t('favorites.clearAll')}
                </Button>
              </Motion.div>
            )}
          </Motion.div>

          {/* --- Temporarily Removed Conditional Loading for Debugging --- */}
          {/* {!isStoreLoaded ? (
            <div className="py-16 text-center text-xl">
              Loading favorites...
            </div>
          ) : */}
          {favorites.length === 0 ? ( // Check if empty
            <Motion.div
              className="py-16 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <p className="mb-6 text-xl">{t('favorites.empty')}</p>
              <Motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a href="/">
                  <Button>{t('favorites.goToGenerator')}</Button>
                </a>
              </Motion.div>
            </Motion.div>
          ) : (
            // Render list directly from store favorites
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {/* Add explicit types for map parameters */}
              {favorites.map((domain: DomainSuggestion, index: number) => (
                <Motion.div
                  key={domain.baseName}
                  className="relative"
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.05, // Staggered animation
                  }}
                  whileHover={{ y: -5 }}
                >
                  {!isFavorite(domain.baseName) && (
                    <Motion.div
                      className="absolute -top-2 -right-2 z-30 rounded-full bg-amber-500 px-2 py-1 text-xs font-medium text-black shadow-md"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.3,
                        delay: 0.3 + index * 0.05,
                      }}
                    >
                      {t('favorites.unfavorited')}
                    </Motion.div>
                  )}
                  {(() => {
                    // Extract TLDs from variations
                    const variationTlds = domain.variations
                      // Add explicit type for map parameter
                      .map((v: DomainSuggestion['variations'][number]) => {
                        const parts = v.name.split('.');
                        return parts.length > 1 ? `.${parts.pop()}` : null; // Ensure it starts with '.'
                      })
                      .filter((tld): tld is string => tld !== null); // Filter out nulls

                    // Validate against known TLDs and cast to Tld type
                    // Add explicit type for filter parameter
                    const validTlds = variationTlds.filter(
                      (tld: string | null): tld is Tld =>
                        tld !== null && TLD_VALUES.includes(tld)
                    );

                    return (
                      <DomainCard
                        domainName={domain.baseName}
                        selectedTld={
                          domain.variations[0]?.name.split('.').pop() || 'com'
                        }
                        variations={domain.variations}
                        availabilityStatus={
                          domain.variations[0]?.available
                            ? 'available'
                            : undefined
                        }
                        selectedTlds={validTlds} // Pass the validated TLDs
                      />
                    );
                  })()}
                </Motion.div>
              ))}
            </div>
          )}
          {/* --- End Temporary Comment --- */}
        </Motion.div>
      </div>
    </Motion.div>
  );
}
